/**
 * Can Doug get there, and does it cost him a day?
 *
 * Doug, 21 September: "I don't want london in there; potsdam, brandenburg,
 * dresden, etc. should all be day trips — this is probably a separate thing
 * that mostly should be reserved for weekend."
 *
 * Three tiers, not two. The app had two — Berlin, or somewhere he happens to
 * be travelling — and everything else was simply hidden. That was wrong in
 * both directions at once:
 *
 *   - **Dresden and Potsdam were being hidden** when they are a train ride
 *     and a Saturday.
 *   - **Potsdam was not being hidden.** `venues.city` had it right all
 *     along — DAS MINSK and ROHKUNSTBAU are Brandenburg — but the feed
 *     read `COALESCE(e.city, v.city)`, so what a source said about the
 *     event beat what is true about the building, and every one of those
 *     sources says Berlin. Fixed in the view, not here.
 *
 * So the city is classified rather than compared, and a day trip is marked
 * as one. What the app then *does* with a day trip — Doug's "reserved for
 * weekend" — is not built yet and is in NEXT.md.
 */
export type Reach = 'city' | 'day_trip' | 'away'

/**
 * The two shapes a reach is computed from — `regions.ts`'s `Region` and
 * `RegionTown`, declared structurally because this file is import-free (the
 * Worker's suite loads it directly) and reads only these fields.
 */
export interface ReachRegion { id: string }
export interface ReachTown { region_id: string; town: string; tier: string }

/**
 * Towns by lowercased name, built once per towns array. `region_towns` has a
 * unique index on `lower(town)` across every region (C0.1), so a name answers
 * at most one row. A WeakMap, so a refetched list builds a fresh index and
 * the old one goes with it.
 */
const townIndex = new WeakMap<readonly ReachTown[], Map<string, ReachTown>>()
function townOf(city: string | null | undefined, towns: readonly ReachTown[]): ReachTown | null {
  const c = (city ?? '').trim().toLowerCase()
  if (!c) return null
  let index = townIndex.get(towns)
  if (!index) {
    index = new Map()
    for (const t of towns) index.set(t.town.trim().toLowerCase(), t)
    townIndex.set(towns, index)
  }
  return index.get(c) ?? null
}

/**
 * The region a town belongs to, or null — a blank city, or a town no region
 * lists (London, Dessau-Roßlau). Lowercased on both sides, because a source
 * writing "POTSDAM" is not a different place. SQL's `region_of(city)` (C0.1)
 * is the same lookup over the same table.
 */
export function regionOf(city: string | null, towns: readonly ReachTown[]): string | null {
  return townOf(city, towns)?.region_id ?? null
}

/**
 * Where a row is, as a tier, seen from the region being looked at.
 *
 * `region_towns` replaced two hand-kept copies of one list — the SQL array
 * in `is_day_trip()` and this file's `DAY_TRIP_CITIES`, held in step by a
 * test (survey trap T4) — with one table: a town is `core` or `day_trip` in
 * exactly one region. A town of the current region at `core` is the city, at
 * `day_trip` a day trip; a town of another region, or of none, is away.
 *
 * A null or blank city is the city **only when `current` is Berlin** (trap
 * T2): most rows carry no city and every source until C1.x is a Berlin
 * source, so a null-city row is a Berlin row — which, seen from Boston, is
 * away. Not "the home region": a Boston-homed person's null-city rows are
 * still Berlin's.
 */
export function reachOf(city: string | null, current: ReachRegion, towns: readonly ReachTown[]): Reach {
  if (!(city ?? '').trim()) return current.id === 'berlin' ? 'city' : 'away'
  const t = townOf(city, towns)
  if (!t || t.region_id !== current.id) return 'away'
  return t.tier === 'day_trip' ? 'day_trip' : 'city'
}


// ---------------------------------------------------------------------------
// Phase 6c: which cinema is nearest
// ---------------------------------------------------------------------------

/** A point on the map. The home point is one of these or null, never a
 *  default: this file used to hold Doug's own building as a constant (his
 *  address is Wolf Kino's), and a second person's "near you" measured from
 *  his flat is worse than none. U1.2: it is `profiles.home_lat/home_lng`,
 *  and `profile.ts` hands it down. `reach.ts` is import-free by rule, so it
 *  takes the value and cannot read it. */
export interface Point { lat: number; lng: number }

/** Straight-line distance from `origin` (the profile's home point, or
 *  something better, per WP 3.4c's Near me — an actual `navigator.geolocation`
 *  fix, when granted), in kilometres. A cinema across town is still a cinema
 *  across town whatever the U-Bahn draws. Null when either coordinate is
 *  missing, so a venue nobody has geocoded yet sorts last rather than first;
 *  null too when there is no origin — no home point means no distance, which
 *  every caller already reads as "draw nothing". */
export function distanceKm(
  lat: number | null,
  lng: number | null,
  origin: Point | null,
): number | null {
  if (lat == null || lng == null || !origin) return null
  const R = 6371
  const toRad = (d: number) => (d * Math.PI) / 180
  const dLat = toRad(lat - origin.lat)
  const dLng = toRad(lng - origin.lng)
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(origin.lat)) * Math.cos(toRad(lat)) * Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

// ---------------------------------------------------------------------------
// Blind spots A3: how far a day trip is
// ---------------------------------------------------------------------------

/** Straight line to road: a detour factor, not a route. */
const ROAD_FACTOR = 1.3
const CAR_KMH = 80
/** Nothing is closer than getting out of town. */
const MIN_TRIP_MIN = 20

/**
 * Minutes by car, estimated: straight-line km from `home` × 1.3, at
 * 80 km/h, rounded **up** to five minutes, never under twenty (K9). Null
 * without coordinates — a venue nobody geocoded gets no figure, not a guess.
 *
 * Up rather than to the nearest: the brief fixes "rounded to 5 minutes"
 * and also expects Potsdam at 30–45, and Barberini is 27 minutes by this
 * formula, which the nearest five makes 25. An estimate you plan a
 * departure from should err late, so the ambiguity goes that way.
 *
 * Every caller labels it "est.": no router, no traffic, no ferry.
 */
export function travelEstimateMin(
  lat: number | null | undefined, lng: number | null | undefined, home: Point | null,
): number | null {
  if (lat == null || lng == null || !home) return null
  const km = distanceKm(lat, lng, home)!
  const min = (km * ROAD_FACTOR / CAR_KMH) * 60
  return Math.max(MIN_TRIP_MIN, Math.ceil(min / 5) * 5)
}

/** "~35 min", "~1 h", "~1 h 05". */
export function formatTripMin(min: number): string {
  if (min < 60) return `~${min} min`
  const h = Math.floor(min / 60)
  const m = min % 60
  return m === 0 ? `~${h} h` : `~${h} h ${String(m).padStart(2, '0')}`
}

/** The fields a trip line reads — `FeedRow` satisfies it structurally, and
 *  every one of them is a `feed_lite` column a cached row already has. */
export interface TripRow {
  city?: string | null
  lat?: number | null
  lng?: number | null
}

/**
 * The trip line: "Chorin · ~55 min by car, est.", on the sheet under the
 * place facts and at the tail of a card's where line. Null for anything that
 * is not a day trip. The one function that builds it, so a transit figure
 * slots in here later and every surface gets it at once.
 *
 * Without coordinates, or without a home to measure from, it is the town
 * alone — still a fact the venue name does not say — rather than a time
 * made up.
 *
 * A day trip of the region being looked at (`current`): Salem from Concord,
 * Potsdam from Berlin.
 */
export function tripLine(
  row: TripRow, home: Point | null, current: ReachRegion, towns: readonly ReachTown[],
): string | null {
  if (reachOf(row.city ?? null, current, towns) !== 'day_trip') return null
  const city = row.city!.trim()
  const min = travelEstimateMin(row.lat, row.lng, home)
  return min == null ? city : `${city} · ${formatTripMin(min)} by car, est.`
}
